/**
 * The pro shop.
 *
 * Four screens behind four tabs, and the first one is the till, because
 * that is the one somebody stands at.
 *
 * ---------------------------------------------------------------------
 * WHAT THE TILL IS FOR
 *
 * A member is standing there holding a jumper. The assistant has about
 * eight seconds before the interaction starts to feel like paperwork.
 * So the till has exactly one thing above the fold — the search box —
 * and everything else follows from what goes in it.
 *
 * The amount still to pay is the largest number on the screen, and it is
 * the only one that changes color. An assistant reading the wrong
 * number here is the whole failure mode of a point of sale.
 *
 * ---------------------------------------------------------------------
 * NOTHING HERE ADDS ANYTHING UP
 *
 * Every total on this page comes back from the server, including after
 * each line is added. That is a round trip the shop can afford and it is
 * the reason the screen cannot ever show a total that the ledger will
 * later disagree with — which, per 0124's self-check, is the one thing a
 * till must never do.
 */

import { useEffect, useState } from 'react';

import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';
import { dateTime, money, parseMoney } from '../lib/format.ts';

type Tab = 'till' | 'stock' | 'catalog' | 'day';

interface Variant {
  variant_id: string;
  sku: string;
  barcode: string | null;
  size: string | null;
  color: string | null;
  price_minor: string;
  stock_on_hand: string;
  outlet_on_hand?: string;
  avg_cost_minor: string;
  margin_pct: string | null;
  item_id: string;
  item_name: string;
  brand: string | null;
  category_code: string;
  category_name: string;
  retail_category_id: string;
  tracks_stock: boolean;
  reorder_point?: string | null;
}

interface Line {
  id: string;
  line_number: number;
  description: string;
  quantity: string;
  list_price_minor: string;
  unit_price_minor: string;
  extended_minor: string;
  tax_minor: string;
  sku: string | null;
  size: string | null;
  color: string | null;
}

interface Tender {
  id: string;
  kind: string;
  amount_minor: string;
  card_brand: string | null;
  card_last4: string | null;
  check_number: string | null;
}

interface Sale {
  sale_id: string;
  ticket_number: string;
  status: string;
  outlet_name: string;
  account_number: string | null;
  account_name: string | null;
  guest_name: string | null;
  member_account_id: string | null;
  subtotal_minor: string;
  discount_minor: string;
  tax_minor: string;
  total_minor: string;
  cost_minor: string;
  completed_at: string | null;
  tender: string | null;
}

interface Ticket {
  sale: Sale;
  lines: Line[];
  tenders: Tender[];
  due_minor: number;
}

const n = (v: string | number | null | undefined) => Number(v ?? 0);

const describe = (v: Variant) =>
  [v.item_name, v.size, v.color].filter(Boolean).join(' · ');

export function ProShopPage() {
  const [tab, setTab] = useState<Tab>('till');
  const { atLeast } = useSession();

  return (
    <>
      <h1>Pro Shop</h1>

      <div className="row" style={{ marginBottom: 16 }}>
        {([
          ['till', 'Till'],
          ['stock', 'Stock'],
          ...(atLeast('manager') ? [['catalog', 'Catalog'] as [Tab, string]] : []),
          // "Takings" is what a British club calls the day's money and
          // means nothing at a club in Illinois. The tab shows the day's
          // sales, so it says so.
          ['day', 'Sales'],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={tab === key ? 'gold small' : 'secondary small'}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'till' && <Till />}
      {tab === 'stock' && <Stock />}
      {tab === 'catalog' && <Catalog />}
      {tab === 'day' && <DaysSales />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The till                                                            */
/* ------------------------------------------------------------------ */

function Till() {
  const { currency, atLeast } = useSession();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Variant[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPay, setShowPay] = useState(false);

  const call = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e as Error);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openTicket = () =>
    call(async () => {
      const t = await api<Ticket>('/v1/retail/sales', { method: 'POST', body: {} });
      setTicket(t);
      setShowPay(false);
      return t;
    });

  // The search box is the whole interface. A scanner types a barcode and
  // presses Enter, which is the same thing as somebody typing "polo".
  const search = async (q: string) => {
    setTerm(q);
    if (q.trim().length < 2) { setResults([]); return; }
    try {
      const r = await api<{ variants: Variant[] }>(
        `/v1/retail/items?q=${encodeURIComponent(q.trim())}&limit=25`);
      setResults(r.variants);
    } catch { /* a failed search is not worth a banner */ }
  };

  const add = (variant: Variant, quantity = 1) =>
    call(async () => {
      let t = ticket;
      if (!t || t.sale.status !== 'open') {
        t = await api<Ticket>('/v1/retail/sales', { method: 'POST', body: {} });
      }
      const next = await api<Ticket>(`/v1/retail/sales/${t.sale.sale_id}/lines`, {
        method: 'POST',
        body: { variant_id: variant.variant_id, quantity },
      });
      setTicket(next);
      setTerm('');
      setResults([]);
      return next;
    });

  // Enter on an exact barcode adds it without a second tap, because that
  // is what a scanner does and the assistant's hands are full.
  const onSearchKey = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const exact = results.find(
      (r) => r.barcode === term.trim() || r.sku.toLowerCase() === term.trim().toLowerCase());
    if (exact) { void add(exact); return; }
    if (results.length === 1 && results[0]) void add(results[0]);
  };

  const removeLine = (lineId: string) =>
    call(async () => {
      const next = await api<Ticket>(
        `/v1/retail/sales/${ticket!.sale.sale_id}/lines/${lineId}`, { method: 'DELETE' });
      setTicket(next);
      return next;
    });

  const complete = () =>
    call(async () => {
      const next = await api<Ticket>(
        `/v1/retail/sales/${ticket!.sale.sale_id}/complete`, { method: 'POST' });
      setTicket(next);
      setShowPay(false);
      return next;
    });

  const sale = ticket?.sale;
  const open = sale?.status === 'open';

  return (
    <>
      <ErrorBanner error={error as never} />

      <Card title="Ring Up">
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="shop-search">Scan or search</label>
            <input
              id="shop-search"
              autoFocus
              value={term}
              placeholder="Barcode, SKU or name"
              onChange={(e) => void search(e.target.value)}
              onKeyDown={(e) => void onSearchKey(e)}
            />
          </div>
          {!open && (
            <button type="button" className="secondary" onClick={() => void openTicket()}>
              New ticket
            </button>
          )}
        </div>

        {results.length > 0 && (
          <table className="data data--cards" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">In stock</th>
                <th className="num">Price</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {results.map((v) => (
                <tr key={v.variant_id}>
                  <td data-label="Product">
                    {describe(v)}
                    <div className="hint">{v.sku}{v.brand ? ` · ${v.brand}` : ''}</div>
                  </td>
                  <td className="num" data-label="In stock">
                    {n(v.outlet_on_hand ?? v.stock_on_hand) < 0
                      ? <Status tone="critical">{n(v.outlet_on_hand ?? v.stock_on_hand)}</Status>
                      : n(v.outlet_on_hand ?? v.stock_on_hand)}
                  </td>
                  <td className="num" data-label="Price">
                    <Money minor={v.price_minor} currency={currency} />
                  </td>
                  <td className="num">
                    <button type="button" className="gold small" disabled={busy}
                      onClick={() => void add(v)}>
                      Add
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {sale && (
        <Card title={`Ticket ${sale.ticket_number}`}>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              {sale.account_name
                ? <><strong>{sale.account_name}</strong> <span className="muted small">
                    {sale.account_number}</span></>
                : sale.guest_name
                  ? <strong>{sale.guest_name}</strong>
                  : <span className="muted">Nobody named yet</span>}
            </div>
            <Status tone={sale.status === 'completed' ? 'good'
              : sale.status === 'voided' ? 'critical' : 'neutral'}>
              {sale.status}
            </Status>
          </div>

          {ticket.lines.length === 0 ? (
            <Empty>Nothing on this ticket yet.</Empty>
          ) : (
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>What</th>
                  <th className="num">Qty</th>
                  <th className="num">Each</th>
                  <th className="num">Amount</th>
                  {open && <th />}
                </tr>
              </thead>
              <tbody>
                {ticket.lines.map((l) => (
                  <tr key={l.id}>
                    <td data-label="What">
                      {l.description}
                      {l.sku && <div className="hint">{l.sku}</div>}
                      {n(l.unit_price_minor) < n(l.list_price_minor) && (
                        <div className="hint">
                          marked down from {money(l.list_price_minor, currency)}
                        </div>
                      )}
                    </td>
                    <td className="num" data-label="Qty">{n(l.quantity)}</td>
                    <td className="num" data-label="Each">
                      <Money minor={l.unit_price_minor} currency={currency} />
                    </td>
                    <td className="num" data-label="Amount">
                      <Money minor={l.extended_minor} currency={currency} signed />
                    </td>
                    {open && (
                      <td className="num">
                        <button type="button" className="secondary small" disabled={busy}
                          onClick={() => void removeLine(l.id)}>Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="tiles" style={{ marginTop: 12 }}>
            <Tile label="Goods" value={money(sale.subtotal_minor, currency)} />
            <Tile label="Tax" value={money(sale.tax_minor, currency)} />
            <Tile
              label={ticket.due_minor === 0 && open ? 'Ready to complete' : 'To pay'}
              value={money(open ? ticket.due_minor : sale.total_minor, currency)}
              tone={ticket.due_minor === 0 ? 'good' : 'warning'}
            />
          </div>

          {ticket.tenders.length > 0 && (
            <p className="muted small">
              Paid: {ticket.tenders.map((t) =>
                `${t.kind}${t.card_last4 ? ` ••${t.card_last4}` : ''} `
                + money(t.amount_minor, currency)).join(' · ')}
            </p>
          )}

          {open && (
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setShowPay((s) => !s)}>
                {showPay ? 'Hide payment' : 'Take payment'}
              </button>
              <button
                type="button"
                className="gold"
                disabled={busy || ticket.due_minor !== 0 || ticket.lines.length === 0}
                onClick={() => void complete()}
              >
                Complete
              </button>
            </div>
          )}

          {sale.status === 'completed' && atLeast('manager') && (
            <VoidTicket saleId={sale.sale_id} onDone={(t) => setTicket(t)} />
          )}
        </Card>
      )}

      {sale && open && showPay && (
        <Payment ticket={ticket} onChange={(t) => setTicket(t)} />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Payment                                                             */
/* ------------------------------------------------------------------ */

/**
 * How it is being paid.
 *
 * The member search and the tender buttons are one card because they are
 * one decision: putting it on an account IS choosing the account.
 *
 * There is no field here for a card number. A card is taken on a
 * terminal; what is typed back is a brand and the last four, and that is
 * all this side is allowed to know.
 */
function Payment({ ticket, onChange }: { ticket: Ticket; onChange: (t: Ticket) => void }) {
  const { currency, term } = useSession();
  const [kind, setKind] = useState<'house' | 'cash' | 'check' | 'card'>('house');
  const [amount, setAmount] = useState('');
  const [brand, setBrand] = useState('');
  const [last4, setLast4] = useState('');
  const [checkNo, setCheckNo] = useState('');
  const [memberTerm, setMemberTerm] = useState('');
  const [members, setMembers] = useState<{ id: string; account_number: string;
    display_name: string }[]>([]);

  const sale = ticket.sale;

  // Seeded from the ticket, and resynced when the ticket moves — a
  // useState initial value is read once, so a form seeded from a prop
  // that arrives later shows the default forever.
  useEffect(() => {
    setAmount(ticket.due_minor > 0 ? String(ticket.due_minor / 100) : '');
  }, [ticket.due_minor]);

  const setBuyer = useMutation(async (accountId: string | null) => {
    const t = await api<Ticket>(`/v1/retail/sales/${sale.sale_id}/buyer`, {
      method: 'PUT',
      body: { member_account_id: accountId },
    });
    onChange(t);
  });

  const pay = useMutation(async () => {
    const minor = parseMoney(amount, currency);
    if (minor === null || minor <= 0) throw new Error('Enter an amount');
    const t = await api<Ticket>(`/v1/retail/sales/${sale.sale_id}/tenders`, {
      method: 'POST',
      body: {
        kind,
        amount_minor: minor,
        ...(kind === 'card'
          ? { card_brand: brand || undefined, card_last4: last4 || undefined,
              processor: 'terminal' }
          : {}),
        ...(kind === 'check' ? { check_number: checkNo || undefined } : {}),
      },
    });
    onChange(t);
  });

  const findMember = async (q: string) => {
    setMemberTerm(q);
    if (q.trim().length < 2) { setMembers([]); return; }
    try {
      const r = await api<{ accounts?: { member_account_id: string; account_number: string;
        display_name: string }[] }>(
        `/v1/member-accounts?search=${encodeURIComponent(q.trim())}&limit=8`);
      setMembers((r.accounts ?? []).map((a) => ({
        id: a.member_account_id, account_number: a.account_number,
        display_name: a.display_name,
      })));
    } catch { setMembers([]); }
  };

  return (
    <Card title="Payment">
      <ErrorBanner error={(pay.error ?? setBuyer.error) as never} />

      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {(['house', 'cash', 'card', 'check'] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={kind === k ? 'gold small' : 'secondary small'}
            onClick={() => setKind(k)}
          >
            {/* Not hardcoded either way. The club's own word, from
                the `term` table (0041): a club in Illinois writes
                checks and a club in Ontario writes the other thing.
                The tender KEY stays 'check' everywhere; only the label
                follows the country. */}
            {k === 'house' ? 'On account'
              : k === 'check' ? term('check')
              : k === 'cash' ? 'Cash'
              : 'Card'}
          </button>
        ))}
      </div>

      {kind === 'house' && (
        <div style={{ marginTop: 12 }}>
          {sale.member_account_id ? (
            <p className="muted small">
              Charging <strong>{sale.account_name}</strong> ({sale.account_number}).{' '}
              <button type="button" className="secondary small"
                onClick={() => setBuyer.run(null)}>Change</button>
            </p>
          ) : (
            <div className="field">
              <label htmlFor="shop-member">Which member</label>
              <input
                id="shop-member"
                value={memberTerm}
                placeholder="Name or account number"
                onChange={(e) => void findMember(e.target.value)}
              />
              {members.length > 0 && (
                <div className="notice" style={{ marginTop: 6 }}>
                  {members.slice(0, 8).map((m) => (
                    <div key={m.id} className="row"
                      style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>{m.display_name} <span className="muted small">
                        {m.account_number}</span></span>
                      <button type="button" className="secondary small"
                        onClick={() => { setBuyer.run(m.id); setMembers([]); setMemberTerm(''); }}>
                        Charge
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {kind === 'card' && (
        <div className="grid-2" style={{ marginTop: 12 }}>
          <div className="field">
            <label htmlFor="shop-brand">Card</label>
            <input id="shop-brand" value={brand} placeholder="Visa"
              onChange={(e) => setBrand(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="shop-last4">Last four</label>
            <input id="shop-last4" value={last4} inputMode="numeric" maxLength={4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, ''))} />
          </div>
          <p className="hint" style={{ gridColumn: '1 / -1' }}>
            Take the card on the terminal. Nothing else about it is kept here, and there
            is nowhere to put it.
          </p>
        </div>
      )}

      {kind === 'check' && (
        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="shop-check">{term('check_number')}</label>
          <input id="shop-check" value={checkNo} onChange={(e) => setCheckNo(e.target.value)} />
        </div>
      )}

      <div className="row" style={{ gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="shop-amount">Amount</label>
          <input id="shop-amount" value={amount} inputMode="decimal"
            onChange={(e) => setAmount(e.target.value)} />
        </div>
        <button
          type="button"
          className="gold"
          disabled={pay.busy || (kind === 'house' && !sale.member_account_id)}
          onClick={() => pay.run()}
        >
          Take {money(parseMoney(amount, currency) ?? 0, currency)}
        </button>
      </div>

      {kind === 'house' && !sale.member_account_id && (
        <p className="hint">Choose a member before charging it to an account.</p>
      )}
    </Card>
  );
}

function VoidTicket({ saleId, onDone }: { saleId: string; onDone: (t: Ticket) => void }) {
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);

  const doVoid = useMutation(async () => {
    const t = await api<Ticket>(`/v1/retail/sales/${saleId}/void`, {
      method: 'POST', body: { reason },
    });
    onDone(t);
    setAsking(false);
  });

  if (!asking) {
    return (
      <div className="actions">
        <button type="button" className="danger secondary" onClick={() => setAsking(true)}>
          Void this ticket
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <ErrorBanner error={doVoid.error as never} />
      <div className="field">
        <label htmlFor="void-reason">Why</label>
        <input id="void-reason" value={reason} autoFocus
          placeholder="Rung up twice" onChange={(e) => setReason(e.target.value)} />
      </div>
      <p className="hint">
        Everything goes back: the charge is reversed, the stock returns to the shelf and
        the cash is taken off the deposit. The ticket stays on the day with the reason
        beside it.
      </p>
      <div className="actions">
        <button type="button" className="secondary" onClick={() => setAsking(false)}>
          Keep it
        </button>
        <button type="button" className="danger" disabled={doVoid.busy || !reason.trim()}
          onClick={() => doVoid.run()}>
          Void
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stock                                                               */
/* ------------------------------------------------------------------ */

function Stock() {
  const { currency, atLeast } = useSession();
  const [reload, setReload] = useState(0);
  const [q, setQ] = useState('');
  const problems = useApi<{ problems: {
    variant_id: string; item_name: string; sku: string; size: string | null;
    color: string | null; on_hand: string; reorder_point: string | null;
    problem: string; outlet_name: string }[] }>('/v1/retail/stock/problems', [reload]);
  const list = useApi<{ variants: Variant[] }>(
    `/v1/retail/items?limit=300${q ? `&q=${encodeURIComponent(q)}` : ''}`, [reload, q]);
  const value = useApi<{ categories: { retail_category_id: string; category_name: string;
    units: string; value_minor: string; retail_minor: string }[] }>(
    atLeast('manager') ? '/v1/retail/stock/value' : null, [reload]);

  return (
    <>
      {(problems.data?.problems.length ?? 0) > 0 && (
        <Card title="Wants Looking At">
          <table className="data data--cards">
            <thead>
              <tr><th>Product</th><th className="num">On hand</th><th>What</th></tr>
            </thead>
            <tbody>
              {problems.data!.problems.map((p) => (
                <tr key={p.variant_id + p.outlet_name}>
                  <td data-label="Product">
                    {[p.item_name, p.size, p.color].filter(Boolean).join(' · ')}
                    <div className="hint">{p.sku} · {p.outlet_name}</div>
                  </td>
                  <td className="num" data-label="On hand">{n(p.on_hand)}</td>
                  <td data-label="What">
                    {p.problem === 'negative'
                      ? <Status tone="critical">Sold more than the count says exists</Status>
                      : <Status tone="warning">At or below reorder point</Status>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="hint">
            A negative is not a bug in the till — it will always sell a member the shirt in
            their hand. It means a delivery was never entered, or a count is wrong.
          </p>
        </Card>
      )}

      {value.data && (
        <Card title="What the Shelf Is Worth">
          <table className="data data--cards">
            <thead>
              <tr>
                <th>Shelf</th><th className="num">Units</th>
                <th className="num">At cost</th><th className="num">At retail</th>
              </tr>
            </thead>
            <tbody>
              {value.data.categories.map((c) => (
                <tr key={c.retail_category_id}>
                  <td data-label="Shelf">{c.category_name}</td>
                  <td className="num" data-label="Units">{n(c.units)}</td>
                  <td className="num" data-label="At cost">
                    <Money minor={c.value_minor} currency={currency} /></td>
                  <td className="num" data-label="At retail">
                    <Money minor={c.retail_minor} currency={currency} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Everything on the Shelf">
        <div className="field">
          <label htmlFor="stock-search">Search</label>
          <input id="stock-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Name, SKU or brand" />
        </div>

        {list.loading && !list.data && <Loading />}
        <ErrorBanner error={list.error} />

        {list.data && (list.data.variants.length === 0
          ? <Empty>Nothing here yet.</Empty>
          : (
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="num">On hand</th>
                  <th className="num">Cost</th>
                  <th className="num">Price</th>
                  <th className="num">Margin</th>
                  {atLeast('manager') && <th />}
                </tr>
              </thead>
              <tbody>
                {list.data.variants.map((v) => (
                  <tr key={v.variant_id}>
                    <td data-label="Product">
                      {describe(v)}
                      <div className="hint">{v.sku} · {v.category_name}</div>
                    </td>
                    <td className="num" data-label="On hand">
                      {n(v.outlet_on_hand ?? v.stock_on_hand) < 0
                        ? <Status tone="critical">{n(v.outlet_on_hand ?? v.stock_on_hand)}</Status>
                        : n(v.outlet_on_hand ?? v.stock_on_hand)}
                    </td>
                    <td className="num" data-label="Cost">
                      <Money minor={Math.round(n(v.avg_cost_minor))} currency={currency} />
                    </td>
                    <td className="num" data-label="Price">
                      <Money minor={v.price_minor} currency={currency} />
                    </td>
                    <td className="num" data-label="Margin">
                      {v.margin_pct === null ? '—' : `${n(v.margin_pct)}%`}
                    </td>
                    {atLeast('manager') && (
                      <td className="num">
                        <Receive variant={v} onDone={() => setReload((k) => k + 1)} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
      </Card>
    </>
  );
}

/**
 * A delivery arriving.
 *
 * The cost is required and there is no default, because whatever is typed
 * here becomes the average that every future sale's margin is measured
 * against — and a zero cost silently makes the shop look like it never
 * paid for anything.
 */
function Receive({ variant, onDone }: { variant: Variant; onDone: () => void }) {
  const { currency } = useSession();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState('1');
  const [cost, setCost] = useState('');

  const receive = useMutation(async () => {
    const unit = parseMoney(cost, currency);
    if (unit === null) throw new Error('What did it cost?');
    await api('/v1/retail/stock/receive', {
      method: 'POST',
      body: {
        variant_id: variant.variant_id,
        quantity: Number(qty),
        unit_cost_minor: unit,
      },
    });
    setOpen(false);
    setCost('');
    onDone();
  });

  if (!open) {
    return (
      <button type="button" className="secondary small" onClick={() => setOpen(true)}>
        Receive
      </button>
    );
  }

  return (
    <div style={{ minWidth: 180 }}>
      <ErrorBanner error={receive.error as never} />
      <div className="field">
        <label htmlFor={`q-${variant.variant_id}`}>How many</label>
        <input id={`q-${variant.variant_id}`} value={qty} inputMode="decimal"
          onChange={(e) => setQty(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={`c-${variant.variant_id}`}>Cost each</label>
        <input id={`c-${variant.variant_id}`} value={cost} inputMode="decimal" autoFocus
          onChange={(e) => setCost(e.target.value)} />
      </div>
      <div className="actions">
        <button type="button" className="secondary small" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button type="button" className="gold small" disabled={receive.busy}
          onClick={() => receive.run()}>
          Receive
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Catalog                                                              */
/* ------------------------------------------------------------------ */

function Catalog() {
  const [reload, setReload] = useState(0);
  const cats = useApi<{ categories: { id: string; code: string; name: string;
    revenue_category: string; items: string; is_active: boolean;
    inventory_account: string; cogs_account: string }[] }>(
    '/v1/retail/categories', [reload]);
  const options = useApi<{
    revenue_categories: { revenue_category: string; account_number: string; name: string }[];
    accounts: { id: string; account_number: string; name: string; account_type: string }[];
    suggested: { inventory_account_id: string | null; cogs_account_id: string | null } | null;
  }>('/v1/retail/category-options', [reload]);

  return (
    <>
      <Card title="Shelves">
        {cats.loading && !cats.data && <Loading />}
        <ErrorBanner error={cats.error} />
        {cats.data && (cats.data.categories.length === 0
          ? <Empty>No shelves yet. Add one below and the till can start selling.</Empty>
          : (
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Shelf</th><th>Revenue goes to</th>
                  <th>Inventory</th><th>Cost of sales</th><th className="num">Products</th>
                </tr>
              </thead>
              <tbody>
                {cats.data.categories.map((c) => (
                  <tr key={c.id}>
                    <td data-label="Shelf">
                      {c.name}<div className="hint">{c.code}</div>
                    </td>
                    <td data-label="Revenue goes to">
                      <span className="mono small">{c.revenue_category}</span>
                    </td>
                    <td data-label="Inventory"><span className="mono small">
                      {c.inventory_account}</span></td>
                    <td data-label="Cost of sales"><span className="mono small">
                      {c.cogs_account}</span></td>
                    <td className="num" data-label="Products">{n(c.items)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
      </Card>

      {options.data && (
        <NewCategory options={options.data} onDone={() => setReload((k) => k + 1)} />
      )}
      {cats.data && cats.data.categories.length > 0 && (
        <NewItem categories={cats.data.categories} onDone={() => setReload((k) => k + 1)} />
      )}
    </>
  );
}

function NewCategory({ options, onDone }: {
  options: {
    revenue_categories: { revenue_category: string; account_number: string; name: string }[];
    accounts: { id: string; account_number: string; name: string; account_type: string }[];
    suggested: { inventory_account_id: string | null; cogs_account_id: string | null } | null;
  };
  onDone: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [revenue, setRevenue] = useState('');
  const [inv, setInv] = useState('');
  const [cogs, setCogs] = useState('');

  // The suggestions arrive after the first render, so they are applied in
  // an effect. A useState initial value is read once and would leave the
  // two hardest fields blank for ever.
  useEffect(() => {
    if (options.suggested?.inventory_account_id) setInv(options.suggested.inventory_account_id);
    if (options.suggested?.cogs_account_id) setCogs(options.suggested.cogs_account_id);
    const shop = options.revenue_categories.find((r) => r.revenue_category.startsWith('pro_shop'));
    if (shop) setRevenue(shop.revenue_category);
  }, [options]);

  const create = useMutation(async () => {
    await api('/v1/retail/categories', {
      method: 'POST',
      body: {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        revenue_category: revenue,
        inventory_account_id: inv,
        cogs_account_id: cogs,
      },
    });
    setCode(''); setName('');
    onDone();
  });

  const assets = options.accounts.filter((a) => a.account_type === 'asset');
  const expenses = options.accounts.filter((a) => a.account_type === 'expense');

  return (
    <Card title="Add a Shelf">
      <ErrorBanner error={create.error as never} />
      <div className="grid-2">
        <div className="field">
          <label htmlFor="cat-code">Short code</label>
          <input id="cat-code" value={code} placeholder="APPAREL"
            onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </div>
        <div className="field">
          <label htmlFor="cat-name">Name</label>
          <input id="cat-name" value={name} placeholder="Apparel"
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cat-rev">Revenue</label>
          <select id="cat-rev" value={revenue} onChange={(e) => setRevenue(e.target.value)}>
            <option value="">Choose…</option>
            {options.revenue_categories.map((r) => (
              <option key={r.revenue_category} value={r.revenue_category}>
                {r.revenue_category} — {r.account_number} {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="cat-inv">Inventory account</label>
          <select id="cat-inv" value={inv} onChange={(e) => setInv(e.target.value)}>
            <option value="">Choose…</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>{a.account_number} {a.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="cat-cogs">Cost of sales account</label>
          <select id="cat-cogs" value={cogs} onChange={(e) => setCogs(e.target.value)}>
            <option value="">Choose…</option>
            {expenses.map((a) => (
              <option key={a.id} value={a.id}>{a.account_number} {a.name}</option>
            ))}
          </select>
        </div>
      </div>
      <p className="hint">
        The revenue mapping already carries the tax code, so the till does not ask about tax
        anywhere — it uses whatever the club charges on that kind of sale.
      </p>
      <div className="actions">
        <button type="button" className="gold" disabled={create.busy || !code || !name || !revenue}
          onClick={() => create.run()}>
          Add shelf
        </button>
      </div>
    </Card>
  );
}

function NewItem({ categories, onDone }: {
  categories: { id: string; name: string }[];
  onDone: () => void;
}) {
  const { currency } = useSession();
  const [cat, setCat] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [price, setPrice] = useState('');
  const [sizes, setSizes] = useState('S, M, L, XL');
  const [color, setColor] = useState('');

  useEffect(() => {
    if (!cat && categories[0]) setCat(categories[0].id);
  }, [categories, cat]);

  const create = useMutation(async () => {
    const unit = parseMoney(price, currency);
    if (unit === null) throw new Error('What does it sell for?');
    const list = sizes.split(',').map((s) => s.trim()).filter(Boolean);
    const base = code.trim().toUpperCase();

    await api('/v1/retail/items', {
      method: 'POST',
      body: {
        retail_category_id: cat,
        code: base,
        name: name.trim(),
        brand: brand.trim() || undefined,
        variants: (list.length ? list : [null]).map((size, i) => ({
          sku: size ? `${base}-${size.toUpperCase().replace(/[^A-Z0-9]/g, '')}` : base,
          size: size ?? undefined,
          color: color.trim() || undefined,
          price_minor: unit,
          sort_order: i,
        })),
      },
    });
    setCode(''); setName(''); setPrice('');
    onDone();
  });

  return (
    <Card title="Add a Product">
      <ErrorBanner error={create.error as never} />
      <div className="grid-2">
        <div className="field">
          <label htmlFor="it-cat">Shelf</label>
          <select id="it-cat" value={cat} onChange={(e) => setCat(e.target.value)}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="it-code">Code</label>
          <input id="it-code" value={code} placeholder="NIKE-QZ"
            onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </div>
        <div className="field">
          <label htmlFor="it-name">Name</label>
          <input id="it-name" value={name} placeholder="Nike quarter-zip"
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="it-brand">Brand</label>
          <input id="it-brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="it-price">Price</label>
          <input id="it-price" value={price} inputMode="decimal" placeholder="89.95"
            onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="it-color">Color</label>
          <input id="it-color" value={color} placeholder="Navy"
            onChange={(e) => setColor(e.target.value)} />
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="it-sizes">Sizes</label>
          <input id="it-sizes" value={sizes} onChange={(e) => setSizes(e.target.value)} />
          <p className="hint">
            One product per size, because that is what has a barcode and a quantity. Leave
            this empty for something that does not come in sizes.
          </p>
        </div>
      </div>
      <div className="actions">
        <button type="button" className="gold"
          disabled={create.busy || !cat || !code || !name || !price}
          onClick={() => create.run()}>
          Add product
        </button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* The day's sales                                                     */
/* ------------------------------------------------------------------ */

function DaysSales() {
  const { currency, term } = useSession();
  const days = useApi<{ days: { business_date: string; outlet_name: string; tickets: string;
    net_minor: string; discount_minor: string; tax_minor: string; gross_minor: string;
    house_minor: string; cash_minor: string; check_minor: string; card_minor: string;
    cost_minor: string; margin_minor: string }[] }>('/v1/retail/day?days=14');
  const sales = useApi<{ sales: Sale[] }>('/v1/retail/sales?days=7&limit=50');

  const today = days.data?.days[0] ?? null;

  return (
    <>
      {today && (
        <Card title={`Sales — ${today.business_date}`}>
          <div className="tiles">
            <Tile label="Tickets" value={n(today.tickets)} />
            <Tile label="Goods" value={money(today.net_minor, currency)} />
            <Tile label="Cost" value={money(today.cost_minor, currency)} />
            <Tile label="Margin" value={money(today.margin_minor, currency)} tone="good" />
          </div>
          <p className="muted small">
            {/* The line the shop counts the drawer against. */}
            On account {money(today.house_minor, currency)} ·
            {' '}Cash {money(today.cash_minor, currency)} ·
            {' '}{term('check')} {money(today.check_minor, currency)} ·
            {' '}Card {money(today.card_minor, currency)}
          </p>
        </Card>
      )}

      <Card title="The Last Two Weeks">
        {days.loading && !days.data && <Loading />}
        <ErrorBanner error={days.error} />
        {days.data && (days.data.days.length === 0
          ? <Empty>Nothing has been sold yet.</Empty>
          : (
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Day</th><th className="num">Tickets</th><th className="num">Goods</th>
                  <th className="num">Cash</th><th className="num">Card</th>
                  <th className="num">Margin</th>
                </tr>
              </thead>
              <tbody>
                {days.data.days.map((d) => (
                  <tr key={d.business_date + d.outlet_name}>
                    <td data-label="Day">{d.business_date}
                      <div className="hint">{d.outlet_name}</div></td>
                    <td className="num" data-label="Tickets">{n(d.tickets)}</td>
                    <td className="num" data-label="Goods">
                      <Money minor={d.net_minor} currency={currency} /></td>
                    <td className="num" data-label="Cash">
                      <Money minor={d.cash_minor} currency={currency} /></td>
                    <td className="num" data-label="Card">
                      <Money minor={d.card_minor} currency={currency} /></td>
                    <td className="num" data-label="Margin">
                      <Money minor={d.margin_minor} currency={currency} signed /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
      </Card>

      <Card title="Recent Tickets">
        {sales.data && (sales.data.sales.length === 0
          ? <Empty>No tickets in the last week.</Empty>
          : (
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Ticket</th><th>Who</th><th>Paid</th>
                  <th className="num">Total</th><th />
                </tr>
              </thead>
              <tbody>
                {sales.data.sales.map((s) => (
                  <tr key={s.sale_id}>
                    <td data-label="Ticket">
                      {s.ticket_number}
                      <div className="hint">
                        {s.completed_at ? dateTime(s.completed_at) : 'not completed'}
                      </div>
                    </td>
                    <td data-label="Who">{s.account_name ?? s.guest_name ?? '—'}</td>
                    <td data-label="Paid">{s.tender ?? '—'}</td>
                    <td className="num" data-label="Total">
                      <Money minor={s.total_minor} currency={currency} signed /></td>
                    <td>
                      <Status tone={s.status === 'completed' ? 'good'
                        : s.status === 'voided' ? 'critical' : 'neutral'}>
                        {s.status}
                      </Status>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
      </Card>
    </>
  );
}
